import { ledger as Ledger, crypto } from '@binance-chain/javascript-sdk'

const DEFAULT_LEDGER_TRANSPORT = 'u2f'
const DEFAULT_LEDGER_OPEN_TIMEOUT = 10000
const DEFAULT_LEDGER_INTERACTIVE_TIMEOUT = 45000
const DEFAULT_LEDGER_NONINTERACTIVE_TIMEOUT = 5000

const LEDGER_EXPECTED_MAJOR_VERSION = 1

export const DEFAULT_GET_ADDRESSES_LIMIT = 4

export class LedgerClient {
  /**
   * Constructs a new LedgerClient.
   * @param {string} transportKey The transport type to use. Valid transports: u2f, wble.
   * @param {number} interactiveTimeout The interactive (user input) timeout in ms.
   * @param {number} nonInteractiveTimeout The non-interactive timeout in ms.
   */
  constructor(
    transportKey = DEFAULT_LEDGER_TRANSPORT,
    interactiveTimeout = DEFAULT_LEDGER_INTERACTIVE_TIMEOUT,
    nonInteractiveTimeout = DEFAULT_LEDGER_NONINTERACTIVE_TIMEOUT
  ) {
    this._transportKey = transportKey
    this._interactiveTimeout = interactiveTimeout
    this._nonInteractiveTimeout = nonInteractiveTimeout
  }

  /**
   * Initialises communication with the Ledger hardware and validates the app version.
   * @param {function} callback The callback that is called when the transport is ready
   * @param {number} openTimeout The open timeout in ms. Default 60s to allow for user actions
   * @throws Will throw Error if communication with the app fails.
   */
  async init(callback, openTimeout = DEFAULT_LEDGER_OPEN_TIMEOUT) {
    if (!this._app) {
      console.debug('LedgerClient: init()')
      let transImpl = Ledger.transports[this._transportKey]
      if (this._transportKey === 'node' && !transImpl) {
        transImpl = require('@ledgerhq/hw-transport-node-hid').default
      }
      const transInst = await transImpl.create(openTimeout)
      // eslint-disable-next-line
      const app = new Ledger.app(transInst, this._interactiveTimeout, this._nonInteractiveTimeout)
      const version = (this._version = await app.getVersion())
      if (version.device_locked) {
        throw new Error('Ledger Device is locked, please unlock it first')
      }
      if (version.major !== LEDGER_EXPECTED_MAJOR_VERSION) {
        throw new Error(
          `Ledger: Expected app major version ${LEDGER_EXPECTED_MAJOR_VERSION}, but got ${version.major}`
        )
      }
      this._app = app
      this._version = version
      this._transport = transInst
    }
    callback && callback.call(this, this._app)
  }

  /**
   * Ensures that the Ledger app client has been initialised, throws an error if not.
   * @return {object} The Ledger app instance
   * @throws Will throw Error if the app has not been initialised
   */
  mustHaveApp() {
    if (this._app === undefined) {
      throw new Error('LedgerClient: Call init() first')
    }
    return this._app
  }

  /**
   * @returns {LedgerApp}
   */
  getApp() {
    return this._app
  }

  /**
   * Gets the version of the Ledger app that is currently open on the device.
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async getVersion() {
    console.debug('LedgerClient: getVersion()')
    this.mustHaveApp()
    if (this._version) {
      return Promise.resolve(this._version)
    }
    return this._app.getVersion()
  }

  /**
   * Gets the public key from the Ledger app that is currently open on the device.
   * @param {array} hdPath The HD path to use to get the public key. Default is [44, 714, 0, 0, 0]
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async getPublicKey(...args) {
    console.debug('LedgerClient: getPublicKey()')
    return this.mustHaveApp().getPublicKey(...args)
  }

  /**
   * Gets the address for the Ledger that has the app open on the device.
   * @param {array} hdPath The HD path to use to get the public key. Default is [44, 714, 0, 0, 0]
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async getAddress(hdPath) {
    console.debug('LedgerClient: getAddress()')
    const response = await this.getPublicKey(hdPath)
    const { pk } = response
    const address = crypto.getAddressFromPublicKey(pk)
    return address
  }

  /**
   * Gets multiple addresses for the device, starting from given index up to `limit` returned items.
   * @param {array} hdPathStart The first HD path to use to get the public key.
   * @param {number} limit The number of addresses to get/generate
   * @return {array} addresses
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async getAddresses(hdPathStart, hrp = 'bnb', limit = DEFAULT_GET_ADDRESSES_LIMIT) {
    console.debug('LedgerClient: getAddresses()')
    if (!Array.isArray(hdPathStart) || hdPathStart.length < 5) {
      throw new Error('hdPathStart must be an array containing at least 5 path nodes')
    }
    const addresses = []
    let hdPath = hdPathStart
    for (let i = 0; i < limit; i++) {
      if (hdPath[hdPath.length - 1] > 0x7fffffff) break

      const response = await this.getPublicKey(hdPath)
      const { pk } = response
      const address = crypto.getAddressFromPublicKey(pk, hrp)
      addresses.push(address)

      hdPath = hdPathStart.slice()
      hdPath[hdPath.length - 1] = i + hdPath[hdPath.length - 1] + 1
    }
    return addresses
  }

  /**
   * Shows the address on the Ledger that has the app open on the device.
   * @param {string} hrp The bech32 hrp (human-readable prefix)
   * @param {array} hdPath The HD path to use for the address. Default is [44, 714, 0, 0, 0]
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async showAddress(...args) {
    console.debug('LedgerClient: showAddress()')
    if (this._version && this._version.major === 1 && this._version.minor === 0) {
      throw new Error('Ledger: showAddress is not supported in app v1.0.x!')
    }
    return this.mustHaveApp().showAddress(...args)
  }

  /**
   * Sends a transaction sign doc to the Ledger app to be signed.
   * @param {Buffer} signBytes The TX sign doc bytes to sign
   * @param {array} hdPath The HD path to use to get the public key. Default is [44, 714, 0, 0, 0]
   * @throws Will throw Error if the app has not been initialised, or if a comms error occurs.
   */
  async sign(...args) {
    console.debug('LedgerClient: sign()')
    return this.mustHaveApp().sign(...args)
  }
}

export default LedgerClient
